<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch, useAttrs, useId } from 'vue'
import { inspectPastedText } from '@/api/pastedText.js'
import { convertedPaste, localTextIssue } from '@/utils/pastedText.js'
defineOptions({ inheritAttrs: false })
const props = defineProps({ modelValue: { type: String, default: '' }, tenantId: String, projectId: String,
  kind: { type: String, default: 'profile' }, multiline: { type: Boolean, default: true }, required: Boolean })
const emit = defineEmits(['update:modelValue', 'source', 'validation', 'blur'])
const attrs = useAttrs()
const uid = useId(), message = ref(''), checked = ref(null), checking = ref(false), converting = ref(false), pending = ref(null)
const touched = ref(false)
const count = computed(() => Array.from(props.modelValue).length)
let timer, sequence = 0, disposed = false, checkedInput = null
const inspect = body => inspectPastedText(props.tenantId, props.projectId, body)
function result(valid, value, error = '', busy = false) {
  message.value = error
  emit('validation', { valid, value, checking: busy, message: error })
  return valid
}
async function validate(showErrors = true) {
  if (showErrors) touched.value = true
  clearTimeout(timer)
  const value = props.modelValue, request = ++sequence
  if (pending.value) return result(false, value, message.value || '粘贴排版尚未识别完成。', converting.value)
  const issue = localTextIssue(value, props.kind, props.required)
  if (issue) return result(false, value, issue)
  if (!props.required && !value.trim()) { checked.value = null; return result(true, value) }
  if (checked.value && checkedInput === value) return result(checked.value.valid, value, checked.value.issues?.join(' ') || '')
  checking.value = true
  result(false, value, '', true)
  try {
    const response = await inspect({ text: value, kind: props.kind })
    if (disposed || request !== sequence || value !== props.modelValue) return false
    checked.value = response
    checkedInput = value
    return result(response.valid, value, response.issues?.join(' ') || '')
  } catch (error) {
    if (request === sequence && !disposed) result(false, value, '暂时无法检查内容，输入已保留，请重新检查。')
    return false
  } finally { if (request === sequence) checking.value = false }
}
function input(event) {
  touched.value = true
  emit('source', null)
  emit('update:modelValue', event.target.value)
}
async function retryPaste() {
  const clipboard = pending.value
  if (!clipboard || converting.value) return
  converting.value = true
  result(false, props.modelValue, '正在识别粘贴排版…', true)
  try {
    const converted = await convertedPaste(clipboard, inspect)
    if (disposed) return
    pending.value = null
    emit('update:modelValue', converted.text)
    emit('source', converted.source)
  } catch (error) { result(false, props.modelValue, error.message) }
  finally {
    converting.value = false
    if (!pending.value && !disposed) { await nextTick(); validate() }
  }
}
function paste(event) {
  const html = event.clipboardData?.getData('text/html')
  if (!html) return
  event.preventDefault()
  touched.value = true
  const value = props.modelValue, start = event.target.selectionStart ?? value.length, end = event.target.selectionEnd ?? start
  pending.value = { value, start, end, text: event.clipboardData.getData('text/plain'), html }
  emit('source', null)
  emit('update:modelValue', value.slice(0, start) + pending.value.text + value.slice(end))
  retryPaste()
}
function undoPaste() {
  emit('update:modelValue', pending.value.value)
  pending.value = null
  validate()
}
watch(() => props.modelValue, value => {
  clearTimeout(timer); ++sequence; checking.value = false; checked.value = null
  result(false, value, localTextIssue(value, props.kind, props.required), true)
  timer = setTimeout(() => validate(false), 350)
}, { immediate: true })
onBeforeUnmount(() => { disposed = true; ++sequence; clearTimeout(timer) })
defineExpose({ validate })
</script>

<template>
  <span class="pasted-field">
    <component :is="multiline ? 'textarea' : 'input'" v-bind="$attrs" :value="modelValue" :required="required"
      :readonly="Boolean(pending)" :aria-invalid="(touched && Boolean(message)) || attrs['aria-invalid']" :aria-describedby="`${attrs['aria-describedby'] || ''} ${uid}-paste-feedback`.trim()"
      @input="input" @paste="paste" @blur="touched = true; validate(); emit('blur')" />
    <span :id="`${uid}-paste-feedback`" class="paste-feedback" aria-live="polite">
      <small v-if="kind === 'material'">{{ count }}/300 字</small>
      <span v-if="touched && message" :class="{ 'form-error': !converting }">{{ message }}</span>
      <small v-else-if="checking">正在检查内容…</small>
      <button v-if="pending && !converting" type="button" @click="retryPaste">重试识别排版</button>
      <button v-if="pending && !converting" type="button" @click="undoPaste">撤销本次粘贴</button>
      <button v-else-if="touched && message && !checking && !converting" type="button" @click="validate">重新检查</button>
      <details v-if="checked?.analysis_text && checked.formatting_detected">
        <summary>已识别排版 · 查看分析文本</summary><pre>{{ checked.analysis_text }}</pre>
      </details>
    </span>
  </span>
</template>

<style scoped>
.pasted-field, .paste-feedback { display: grid; gap: 6px; min-width: 0; }
.pasted-field > input, .pasted-field > textarea { width: 100%; box-sizing: border-box; padding: 13px 14px; border: 1px solid #c8dada; border-radius: 13px; color: var(--ink); background: #fbfdfd; font: inherit; resize: vertical; }
.pasted-field > :focus { outline: 2px solid var(--teal); outline-offset: 1px; }
.paste-feedback { font-size: 13px; line-height: 1.6; font-weight: normal; }
.paste-feedback pre { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 240px; overflow: auto; }
.pasted-field > [aria-invalid="true"] { border-color: #b42318; }
button { justify-self: start; cursor: pointer; }
</style>
